import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Callout } from '../../components/Callout';
import { Playground } from '../../components/Playground';

export function XssPage(): Node {
  return html`
    <div>
      <h1>XSS Prevention</h1>
      <p>onefold is secure by default. Text interpolation uses <code>textContent</code>, making XSS structurally impossible in the default path.</p>

      <h2>How onefold Prevents XSS</h2>
      <ul>
        <li><strong>textContent by default</strong> — string interpolations in templates set <code>textContent</code>, not <code>innerHTML</code>. HTML in user data renders as text.</li>
        <li><strong>No eval()</strong> — no dynamic code execution anywhere in the framework.</li>
        <li><strong>No innerHTML</strong> — the template engine constructs real DOM nodes, never parses HTML strings.</li>
        <li><strong>CSP compatible</strong> — works with strict Content-Security-Policy headers out of the box.</li>
      </ul>

      ${CodeBlock(`import { html } from 'onefold';

const userInput = '<script>alert("xss")</script>';

// SAFE — renders as text, not HTML
html\`<p>\${userInput}</p>\`;
// Result: <p>&lt;script&gt;alert("xss")&lt;/script&gt;</p>`)}

      ${Callout('Unlike frameworks that use innerHTML or dangerouslySetInnerHTML, onefold never interprets strings as HTML. This eliminates the most common XSS vector.', 'info')}

      <h2>Sanitization for Raw HTML</h2>
      <p>If you must render trusted HTML (e.g., from a CMS), sanitize it first:</p>
      ${CodeBlock(`import { html } from 'onefold';

// Use DOMPurify or similar library
import DOMPurify from 'dompurify';

function RichContent(rawHtml: string): Node {
  const clean = DOMPurify.sanitize(rawHtml);
  const container = document.createElement('div');
  container.innerHTML = clean;
  return container;
}`)}

      <h2>Trusted Types</h2>
      <p>onefold is compatible with the Trusted Types API for defense in depth:</p>
      ${CodeBlock(`// CSP header
Content-Security-Policy: require-trusted-types-for 'script'

// onefold never triggers Trusted Types violations because
// it never assigns to innerHTML, outerHTML, or similar sinks.`)}

      <h2>Try It</h2>
      <p>Type HTML/script into the input — see how onefold escapes it automatically vs <code>raw()</code>:</p>

      ${Playground(`import { html, mount, createSignal, raw } from 'onefold';

function App() {
  const userInput = createSignal('<img src=x onerror="alert(1)">');

  const examples = [
    '<script>alert("xss")<\/script>',
    '<img src=x onerror="alert(1)">',
    '<a href="javascript:alert(1)">Click me</a>',
    '<div onmouseover="alert(1)">Hover</div>',
    'Hello <b>world</b>!',
  ];

  return html\`
    <div>
      <h3>XSS Prevention Demo</h3>
      <div style="margin-bottom:12px">
        <label style="font-size:13px;font-weight:600;display:block;margin-bottom:4px">User input (try pasting malicious HTML):</label>
        <input
          oninput=\${(e) => userInput.set(e.target.value)}
          style="width:100%"
          placeholder="Type or paste HTML here..."
        />
        <div style="display:flex;gap:4px;margin-top:6px;flex-wrap:wrap">
          \${examples.map(ex => html\`
            <button onclick=\${() => userInput.set(ex)} style="font-size:11px;padding:2px 6px">\${ex.slice(0, 20)}...</button>
          \`)}
        </div>
      </div>

      <div style="display:grid;gap:12px;grid-template-columns:1fr 1fr">
        <div style="padding:12px;border:1px solid #86efac;border-radius:8px;background:#f0fdf4">
          <h4 style="font-size:13px;color:#166534;margin-bottom:6px">Default (textContent) — SAFE</h4>
          <div style="font-size:13px;font-family:monospace;word-break:break-all">\${() => userInput()}</div>
        </div>
        <div style="padding:12px;border:1px solid #fca5a5;border-radius:8px;background:#fef2f2">
          <h4 style="font-size:13px;color:#991b1b;margin-bottom:6px">raw() — Sanitized HTML</h4>
          <div style="font-size:13px">\${() => raw(userInput())}</div>
        </div>
      </div>

      <p style="font-size:12px;color:#666;margin-top:12px">
        Left: XSS impossible (rendered as text). Right: raw() strips scripts/event handlers but allows safe HTML tags.
      </p>
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'XSS Prevention — textContent vs raw()')}
  `;
}
